import * as i0 from '@angular/core';
import { Injectable, NgModule } from '@angular/core';
import { BQXConfiguration } from 'bqx-ui-common';
import { BehaviorSubject, Subject, ReplaySubject, from, Observable, of, throwError } from 'rxjs';
import { getItem, setItem, init } from '@boldpenguin/sdk-core';
import { getPartnerEngineConfig, getStore, updateActiveQuestionSet, updateActiveAnswerId, getQuestionSetByCode, updateAnswer, resetApplication, loadSingleApplicationForm, loadSingleApplicationFormFromToken, getApplicationForm, getQuestionSets, isFormLoading, isActiveQuestionSetLoading, areQuotesLoading, getErrors, getQuotes, getCurrentActiveQuestionSet, getCompletedQuestionSets, getCurrentActiveQuestionSetId, getApplicationState, getWebsocketsCableToken, doesUserOwnApplicationForm, isFormLocked, isFormTakenOver, isFormCloned, isFormInactive, isFormLoaded, getCarriersProducts, getMessages, cloneApplicationForm } from '@boldpenguin/sdk-redux';
import { ApplicationFormQuestionSet, QuoteRequest, ApplicationForm } from '@boldpenguin/sdk-requests';
import { takeUntil, switchMap } from 'rxjs/operators';
import { logout, login } from '@boldpenguin/sdk-auth';
import { BpEnvironmentName } from '@boldpenguin/sdk-types';
import { setItem as setItem$1 } from '@boldpenguin/sdk-utility';
import { HttpHeaders, HttpParams } from '@angular/common/http';

class BqxApplicationAbstractService {
}

var LocalStorage;
(function (LocalStorage) {
    LocalStorage["APPFORM_TOKEN"] = "token";
    LocalStorage["APPFORM_TOKEN_EXPIRES_AT"] = "expiresAt";
})(LocalStorage || (LocalStorage = {}));
var SessionStorage;
(function (SessionStorage) {
    SessionStorage["APPFORM_UUI_DTOKEN"] = "app-uid-token";
    SessionStorage["APPFORM_TOKEN_ID"] = "token_id";
})(SessionStorage || (SessionStorage = {}));
var RuleEngineName;
(function (RuleEngineName) {
    RuleEngineName["BOLDPENGUIN"] = "BP";
    RuleEngineName["PROGRESSIVE"] = "PGR";
})(RuleEngineName || (RuleEngineName = {}));
var BQXEnvironment;
(function (BQXEnvironment) {
    BQXEnvironment["PROD"] = "Production";
    BQXEnvironment["DEV"] = "Dev";
    BQXEnvironment["QA"] = "Qa";
    BQXEnvironment["Lab"] = "Lab";
})(BQXEnvironment || (BQXEnvironment = {}));
var ApplicationType;
(function (ApplicationType) {
    ApplicationType["AGENT"] = "Agent";
    ApplicationType["CONSUMER"] = "Consumer";
})(ApplicationType || (ApplicationType = {}));

class UtillCommon {
    static get appFormUiIdToken() {
        return sessionStorage.getItem(SessionStorage.APPFORM_UUI_DTOKEN);
    }
    static set appFormUiIdToken(value) {
        sessionStorage.setItem(SessionStorage.APPFORM_UUI_DTOKEN, value);
    }
    static get appFormTokenId() {
        return sessionStorage.getItem(SessionStorage.APPFORM_TOKEN_ID);
    }
    static set appFormTokenId(value) {
        sessionStorage.setItem(SessionStorage.APPFORM_TOKEN_ID, value);
    }
    static get appFormToken() {
        return localStorage.getItem(LocalStorage.APPFORM_TOKEN);
    }
    static set appFormToken(value) {
        localStorage.setItem(LocalStorage.APPFORM_TOKEN, value);
    }
    static get appFormTokenExpireAt() {
        return localStorage.getItem(LocalStorage.APPFORM_TOKEN_EXPIRES_AT) ?
            JSON.parse(localStorage.getItem(LocalStorage.APPFORM_TOKEN_EXPIRES_AT)) :
            null;
    }
    static set appFormTokenExpireAt(value) {
        localStorage.setItem(LocalStorage.APPFORM_TOKEN_EXPIRES_AT, value);
    }
    static isNullOrEmpty(obj) {
        let result = false;
        switch (obj) {
            case null: {
                result = true;
                break;
            }
            case undefined: {
                result = true;
                break;
            }
            case 'undefined': {
                result = true;
                break;
            }
            default: {
                result = false;
                break;
            }
        }
        return result;
    }
}

class BPApplicationForm {
    get config() {
        switch (this._applicationType) {
            case ApplicationType.AGENT: {
                this._Config = getPartnerEngineConfig(getStore().getState());
                break;
            }
            case ApplicationType.CONSUMER: {
                const { config: { env, uri } } = getStore().getState();
                this._Config = {
                    token: UtillCommon.appFormToken,
                    env,
                    uri
                };
                break;
            }
            default: {
                this._Config = getPartnerEngineConfig(getStore().getState());
                break;
            }
        }
        return this._Config;
    }
    get isOwner$() {
        return this._isOwner$.asObservable();
    }
    get isLocked$() {
        return this._isLocked$.asObservable();
    }
    get isTakenOver$() {
        return this._isTakenOver$.asObservable();
    }
    get isFormCloned$() {
        return this._isFormCloned$.asObservable();
    }
    get isFormInactive$() {
        return this._isFormInactive$.asObservable();
    }
    get isFormLoaded$() {
        return this._isFormLoaded$.asObservable();
    }
    get getMessages$() {
        return this._getMessages$.asObservable();
    }
    get carrierProducts$() {
        return this._carrierProducts$.asObservable();
    }
    get isQuestionCompleted$() {
        return this._isQuestionSetCompleted$.asObservable();
    }
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    constructor() {
        this.applicationQuestionSet$ = new BehaviorSubject(null);
        this.applicationQuestionSetId$ = new BehaviorSubject(null);
        this.applicationForm$ = new BehaviorSubject(null);
        this.applicationQuestionSets$ = new BehaviorSubject([]);
        this.applicationFormErrorsSet$ = new BehaviorSubject(null);
        this.completedQuestionSets$ = new BehaviorSubject([]);
        this.applicationQuoteRequests$ = new BehaviorSubject([]);
        this.isApplicationQuotesLoading$ = new BehaviorSubject(false);
        this.isQuestionSetLoading$ = new BehaviorSubject(false);
        this.isApplicationFormLoading$ = new BehaviorSubject(false);
        this.applicationState$ = new BehaviorSubject(null);
        this.applicationTokenId$ = new BehaviorSubject('');
        this._tokenId$ = new BehaviorSubject('');
        this._saveQSetUnsub$ = new Subject();
        this._bindQuoteUnsub$ = new Subject();
        this._isOwner$ = new ReplaySubject();
        this._isLocked$ = new ReplaySubject();
        this._isTakenOver$ = new ReplaySubject();
        this._isFormCloned$ = new ReplaySubject();
        this._isFormInactive$ = new ReplaySubject();
        this._isFormLoaded$ = new ReplaySubject();
        this._getMessages$ = new BehaviorSubject([]);
        this._carrierProducts$ = new BehaviorSubject([]);
        this._isQuestionSetCompleted$ = new ReplaySubject();
    }
    static getInstance() {
        if (!BPApplicationForm.instance) {
            BPApplicationForm.instance = new BPApplicationForm();
        }
        return BPApplicationForm.instance;
    }
    initApplication(options) {
        const { applicationType, prefillData } = UtillCommon.isNullOrEmpty(options) ? { applicationType: '', prefillData: {} } : options;
        this._applicationType = applicationType;
        this._prefilldata = prefillData;
        if (!UtillCommon.isNullOrEmpty(UtillCommon.appFormUiIdToken))
            this.getApplication();
        else
            this.createApplication();
    }
    createApplication() {
        switch (this._applicationType) {
            case ApplicationType.AGENT: {
                this.createAgentApplicationForm();
                break;
            }
            case ApplicationType.CONSUMER: {
                this.getConsumerApplication();
                break;
            }
            default: {
                this.createAgentApplicationForm();
                break;
            }
        }
    }
    getApplication() {
        switch (this._applicationType) {
            case ApplicationType.AGENT: {
                this.getAgentApplication();
                break;
            }
            case ApplicationType.CONSUMER: {
                this.getConsumerApplication();
                break;
            }
            default: {
                this.getAgentApplication();
                break;
            }
        }
    }
    updateActiveQuestionSet(code) {
        const questionSets = this.applicationQuestionSets$.value.filter((queSet) => queSet.question_set.code == code);
        if (questionSets?.length > 0) {
            getStore().dispatch(updateActiveQuestionSet(questionSets[0].question_set_id));
        }
    }
    updateActiveAnswer(id) {
        getStore().dispatch(updateActiveAnswerId(id));
    }
    /**
     * @description to get the answer by question Id
     * @param question_id
     * @returns IAnswer if questionId matches in current question set or returns null
     */
    getQuestionById(questionId) {
        if (this.applicationQuestionSet$.value)
            return this.applicationQuestionSet$.value.answers.find((ans) => ans.question.id == questionId);
        else
            return null;
    }
    /**
     * @description to get the answer by question code
     * @param code
     * @returns IAnswer if answer code matches in current question set or returns null
     */
    getQuestionByCode(code) {
        if (this.applicationQuestionSet$.value)
            return this.applicationQuestionSet$.value.answers.find((ans) => ans.question.code == code);
        else
            return null;
    }
    /**
     * @description to get the question observable set by question set code
     * @param code question set codeF
     * @returns Observable<IApplicationFormQuestionSet> if question code matches in current sdk state or returns null
     */
    getQuestionSetByCode(code) {
        const qSet$ = new ReplaySubject();
        getStore().subscribe(() => {
            const qSet = getQuestionSetByCode(getStore().getState(), code);
            if (qSet)
                qSet$.next(qSet);
        });
        return qSet$;
    }
    /**
     * @description to save the answer using redux dispatch
     * @param answer
     * @returns void
     */
    saveAnswer(answer) {
        const par = { config: this.config, answer };
        getStore().dispatch(updateAnswer(par));
    }
    /**
     * @description to save the individual question set using SDk request API
     * @param
     * @returns
     */
    saveQuestionSet(code) {
        let questionSet;
        if (code) {
            questionSet = this.applicationQuestionSets$.value.filter((question) => question.question_set.code === code)[0];
        }
        else {
            questionSet = this.applicationQuestionSet$.value;
        }
        const tempQSet = { ...questionSet };
        tempQSet.is_completed = true;
        return from(ApplicationFormQuestionSet.updateQuestionSet(tempQSet, this.config));
    }
    resetApplicationForm() {
        getStore().dispatch(resetApplication());
        this.initStoreListener();
    }
    bindQuote(appFormId, quoteId) {
        return new Observable((observer) => {
            from(QuoteRequest.bindQuote(quoteId, appFormId, this.config))
                .pipe(takeUntil(this._bindQuoteUnsub$))
                .subscribe((val) => {
                observer.next(val.parsedBody.retrieve_url);
                observer.complete();
            }, (err) => {
                observer.error(err);
                observer.complete();
            });
        });
    }
    //TODO: Unit test will be covered separately as part of the individual card/effort as there were no base unit test setup for this service class.
    //Modified the return type to observable that will provide async callback operation at client application
    skipCurrentTier(appFormId) {
        return from(ApplicationForm.getApplicationForm(appFormId, this.config)).pipe(switchMap((response) => {
            return from(ApplicationForm.createCurrentTierSkips(response.parsedBody, this.config));
        }));
    }
    createAgentApplicationForm() {
        from(ApplicationForm.createApplicationForm(this._prefilldata, this.config)).subscribe((resp) => {
            UtillCommon.appFormUiIdToken = resp.parsedBody.id;
            getStore().dispatch(loadSingleApplicationForm({
                config: this.config,
                id: resp.parsedBody.id
            }));
            this.initStoreListener();
        }, (err) => {
            console.log('Create Application Failed:', err);
        });
    }
    getConsumerApplication() {
        const token = UtillCommon.appFormToken ? UtillCommon.appFormToken : getItem(LocalStorage.APPFORM_TOKEN);
        const token_id = UtillCommon.appFormTokenId ? UtillCommon.appFormTokenId : getItem(SessionStorage.APPFORM_TOKEN_ID);
        const configWithTokens = { ...this.config, ...{ token, token_id } };
        getStore().dispatch(loadSingleApplicationFormFromToken(configWithTokens));
        this.initStoreListener();
        this.setupStoreForTesting();
    }
    getAgentApplication() {
        if (!UtillCommon.isNullOrEmpty(UtillCommon.appFormUiIdToken)) {
            getStore().dispatch(loadSingleApplicationForm({ config: this.config, id: UtillCommon.appFormUiIdToken }));
            this.initStoreListener();
            this.setupStoreForTesting();
        }
        else {
            console.log('No token is available to get the Application.');
        }
    }
    initStoreListener() {
        getStore().subscribe(() => this.ListenChangeInState());
    }
    /**
     * @description Provides cypress access to redux store if debug flag is true
     */
    setupStoreForTesting() {
        try {
            const configNode = getStore().getState().config;
            if (window.Cypress && configNode && configNode.debug === true) {
                window.store = getStore();
            }
        }
        catch (error) {
            console.error(error);
        }
    }
    ListenChangeInState() {
        const appform = getApplicationForm(getStore().getState());
        if (this.applicationForm$.value !== appform) {
            this.applicationForm$.next(appform);
            //This is needed for search and takeover scenario
            if (appform?.id)
                sessionStorage.setItem(SessionStorage.APPFORM_UUI_DTOKEN, appform.id);
        }
        const appQuestionSets = getQuestionSets(getStore().getState());
        if (this.applicationQuestionSets$.value !== appQuestionSets) {
            this.applicationQuestionSets$.next(appQuestionSets);
        }
        const appFormLoading = isFormLoading(getStore().getState());
        if (this.isApplicationFormLoading$.value !== appFormLoading) {
            this.isApplicationFormLoading$.next(appFormLoading);
        }
        const activeQuestionSetLoading = isActiveQuestionSetLoading(getStore().getState());
        if (this.isQuestionSetLoading$.value !== activeQuestionSetLoading) {
            this.isQuestionSetLoading$.next(activeQuestionSetLoading);
        }
        const quotesLoading = areQuotesLoading(getStore().getState());
        if (this.isApplicationQuotesLoading$.value !== quotesLoading) {
            this.isApplicationQuotesLoading$.next(quotesLoading);
        }
        const appCurrentErrorsSet = getErrors(getStore().getState());
        if (this.applicationFormErrorsSet$.value !== appCurrentErrorsSet) {
            this.applicationFormErrorsSet$.next(appCurrentErrorsSet);
        }
        const quotes = getQuotes(getStore().getState());
        if (this.applicationQuoteRequests$.value !== quotes) {
            if (quotes.length > 0 && (appform.state == 'completed' || appform.state == 'quoted')) {
                this.applicationQuoteRequests$.next(quotes);
            }
        }
        const appFormQuestionSet = getCurrentActiveQuestionSet(getStore().getState());
        if (this.applicationQuestionSet$.value !== appFormQuestionSet) {
            if (appFormQuestionSet) {
                this._isQuestionSetCompleted$.next(appFormQuestionSet.is_completed);
                if (!appFormQuestionSet.is_completed) {
                    this.applicationQuestionSet$.next(appFormQuestionSet);
                }
            }
        }
        const completedQuestionSet = getCompletedQuestionSets(getStore().getState());
        if (this.completedQuestionSets$.value !== completedQuestionSet) {
            this.completedQuestionSets$.next(completedQuestionSet);
        }
        const appFormQuestionSetId = getCurrentActiveQuestionSetId(getStore().getState());
        if (this.applicationQuestionSetId$.value !== appFormQuestionSetId) {
            this.applicationQuestionSetId$.next(appFormQuestionSetId);
        }
        const appFormState = getApplicationState(getStore().getState());
        if (this.applicationState$.value !== appFormState) {
            if (appFormState) {
                this.applicationState$.next(appFormState);
            }
        }
        const tokenId = getWebsocketsCableToken(getStore().getState());
        if (this.applicationTokenId$.value !== tokenId) {
            setItem(SessionStorage.APPFORM_TOKEN_ID, tokenId);
            UtillCommon.appFormTokenId = tokenId;
            this.applicationTokenId$.next(tokenId);
        }
        const isOwner = doesUserOwnApplicationForm(getStore().getState());
        this._isOwner$.next(isOwner);
        const isLocked = isFormLocked(getStore().getState());
        this._isLocked$.next(isLocked);
        const isTakenOver = isFormTakenOver(getStore().getState());
        this._isTakenOver$.next(isTakenOver);
        const isCloned = isFormCloned(getStore().getState());
        this._isFormCloned$.next(isCloned);
        const isInactive = isFormInactive(getStore().getState());
        this._isFormInactive$.next(isInactive);
        const isLoaded = isFormLoaded(getStore().getState());
        this._isFormLoaded$.next(isLoaded);
        const carriersProducts = getCarriersProducts(getStore().getState());
        if (this._carrierProducts$.value !== carriersProducts) {
            if (carriersProducts.length > 0) {
                this._carrierProducts$.next(carriersProducts);
            }
        }
        const get_Messages = getMessages(getStore().getState());
        this._getMessages$.next(get_Messages);
    }
}

class BPSDKConnection {
    get sdktoken() {
        return getItem(LocalStorage.APPFORM_TOKEN);
    }
    get sdktokenExpireAt() {
        return getItem(LocalStorage.APPFORM_TOKEN_EXPIRES_AT) ? JSON.parse(getItem(LocalStorage.APPFORM_TOKEN_EXPIRES_AT)) : null;
    }
    get BPEnvironmanet() {
        switch (BQXConfiguration.data.environment) {
            case BQXEnvironment.PROD: {
                this._BPEnv = BpEnvironmentName.Production;
                break;
            }
            case BQXEnvironment.QA: {
                this._BPEnv = BpEnvironmentName.Beta;
                break;
            }
            case BQXEnvironment.DEV: {
                this._BPEnv = BpEnvironmentName.Development;
                break;
            }
            default: {
                this._BPEnv = BpEnvironmentName.Beta;
                break;
            }
        }
        return this._BPEnv;
    }
    constructor() {
        this.source = BQXConfiguration.data.applicationFormSource;
        this.configOption = {
            authUri: BQXConfiguration.data.applicationFormAuthUri,
            clientId: BQXConfiguration.data.applicationFormClientId,
            debug: BQXConfiguration.data.applicationFormStateLog,
            env: this.BPEnvironmanet,
            uri: BQXConfiguration.data.applicationFormApiUri,
            wsBaseUri: BQXConfiguration.data.applicationFormWsUri,
            options: { hideNestedQuestionGroupLabels: true }
        };
    }
    static getInstance() {
        if (!BPSDKConnection.instance) {
            BPSDKConnection.instance = new BPSDKConnection();
        }
        return BPSDKConnection.instance;
    }
    connect(options) {
        const { applicationType } = UtillCommon.isNullOrEmpty(options) ? { applicationType: '' } : options;
        switch (applicationType) {
            case ApplicationType.AGENT: {
                return this.initAgent();
            }
            case ApplicationType.CONSUMER: {
                return this.initConsumer();
            }
            default: {
                return this.initAgent();
            }
        }
    }
    disconnect() {
        try {
            logout(this.configOption);
            return this.isSdkAuthenticated();
        }
        catch (error) {
            console.log('BP Logout Failed:', error);
            return of(false);
        }
    }
    initAgent() {
        try {
            init(this.configOption);
            this.isSdkAuthenticated().subscribe((val) => {
                if (!val) {
                    login(this.configOption);
                }
            });
            return this.isSdkAuthenticated();
        }
        catch (error) {
            console.log('BP Authentication Failed:', error);
            return of(false);
        }
    }
    initConsumer() {
        try {
            setItem$1(LocalStorage.APPFORM_TOKEN, UtillCommon.appFormToken);
            init(this.configOption);
            setItem$1(SessionStorage.APPFORM_TOKEN_ID, UtillCommon.appFormTokenId);
            return this.isSdkAuthenticated();
        }
        catch (error) {
            console.log('BP Authentication Failed:', error);
            return of(false);
        }
    }
    isSdkAuthenticated() {
        return new Observable((observer) => {
            const intervalId = setInterval(() => {
                const expires = this.sdktokenExpireAt;
                const token = this.sdktoken;
                if (UtillCommon.isNullOrEmpty(token)) {
                    observer.next(false);
                    observer.complete();
                    clearInterval(intervalId);
                    return;
                }
                const authenticated = !!(token && expires > Date.now());
                observer.next(authenticated);
                if (authenticated) {
                    observer.complete();
                    clearInterval(intervalId);
                }
            }, 1000);
        });
    }
}

class BpApplicationSearchService {
    get config() {
        switch (BpApplicationSearchService._applicationType) {
            case ApplicationType.AGENT: {
                this._Config = getPartnerEngineConfig(getStore().getState());
                break;
            }
            case ApplicationType.CONSUMER: {
                const { config: { env, uri } } = getStore().getState();
                this._Config = {
                    token: UtillCommon.appFormToken,
                    env,
                    uri
                };
                break;
            }
            default: {
                this._Config = getPartnerEngineConfig(getStore().getState());
                break;
            }
        }
        return this._Config;
    }
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    constructor() { }
    static getInstance(options) {
        if (!BpApplicationSearchService.instance) {
            BpApplicationSearchService.instance = new BpApplicationSearchService();
            const { applicationType } = options;
            BpApplicationSearchService._applicationType = applicationType;
        }
        return BpApplicationSearchService.instance;
    }
    getSearchOptions(http, params) {
        if (BQXConfiguration.data.applicationFormTenant)
            return http.get(this.config.uri + '/searchable_questions/' + BQXConfiguration.data.applicationFormTenant + '/questions/', {
                headers: this.generateHeaders(),
                params: this.generateparam(params),
                responseType: 'json',
                observe: 'response'
            });
        else {
            //TODO Logging
            return throwError('No Tenenat ID Provided');
        }
    }
    cloneApplicationForm(params) {
        const { appFormId } = params;
        getStore().dispatch(cloneApplicationForm({ config: this.config, appFormId }));
    }
    searchApplicationForms(params) {
        const { sortColumn, sortOrder, pageNumber, pageSize, answers, q } = params
            ? params
            : {
                sortColumn: undefined,
                sortOrder: undefined,
                pageNumber: undefined,
                pageSize: undefined,
                answers: undefined,
                q: undefined
            };
        return ApplicationForm.searchApplicationForms(this.config, { sortColumn, sortOrder, pageNumber, pageSize, answers, q });
    }
    generateHeaders() {
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + this.config.token
        });
        return headers;
    }
    generateparam(params) {
        let httpParam = new HttpParams();
        Object.keys(params).forEach((key) => {
            httpParam = httpParam.append(key, params[key]);
        });
        return httpParam;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: BpApplicationSearchService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: BpApplicationSearchService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: BpApplicationSearchService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [] });

class BqxApplicationService {
    constructor() {
        this._isConnected$ = new BehaviorSubject(false);
    }
    get isConnected() {
        return this._isConnected$;
    }
    connect(options) {
        switch (BQXConfiguration.data.applicationFormSource) {
            case RuleEngineName.BOLDPENGUIN: {
                this._connection = BPSDKConnection.getInstance();
                break;
            }
            default: {
                this._connection = null;
                break;
            }
        }
        if (this._connection && this._connection !== null) {
            this._connection.connect(options).subscribe((val) => {
                this._isConnected$.next(val);
            });
        }
        return this._isConnected$;
    }
    disConnect(options) {
        switch (BQXConfiguration.data.applicationFormSource) {
            case RuleEngineName.BOLDPENGUIN: {
                if (this._connection && this._connection !== null)
                    this._connection.disconnect(options).subscribe((val) => {
                        this._isConnected$.next(val);
                    });
                break;
            }
            default: {
                this._isConnected$.next(false);
                break;
            }
        }
        return this._isConnected$;
    }
    getApplicationForm() {
        if (this._connection !== null)
            switch (this._connection.source) {
                case RuleEngineName.BOLDPENGUIN: {
                    this._applicationForm = BPApplicationForm.getInstance();
                    break;
                }
                default: {
                    break;
                }
            }
        if (this._applicationForm)
            return this._applicationForm;
        else
            return null;
    }
    getSearchForm(options) {
        if (this._connection !== null)
            switch (this._connection.source) {
                case RuleEngineName.BOLDPENGUIN: {
                    this._searchForm = BpApplicationSearchService.getInstance(options);
                    break;
                }
                default: {
                    break;
                }
            }
        if (this._searchForm)
            return this._searchForm;
        else
            return null;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: BqxApplicationService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: BqxApplicationService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: BqxApplicationService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

class BqxApplicationModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: BqxApplicationModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.8", ngImport: i0, type: BqxApplicationModule }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: BqxApplicationModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.8", ngImport: i0, type: BqxApplicationModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [],
                    imports: [],
                    exports: []
                }]
        }] });

/*
 * Public API Surface of bqx-application
 */

/**
 * Generated bundle index. Do not edit.
 */

export { BqxApplicationAbstractService, BqxApplicationModule, BqxApplicationService };
